import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Search } from 'lucide-react'
import { useState } from 'react'
import { SHORTCUT_GROUPS, filterShortcuts } from './keyboardShortcuts'

interface KeyboardShortcutsDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
}

export function KeyboardShortcutsDialog({ open, onOpenChange }: KeyboardShortcutsDialogProps) {
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className='flex h-[min(640px,85vh)] flex-col gap-3 p-0' data-testid='shortcuts-dialog'>
				<ShortcutsList />
			</DialogContent>
		</Dialog>
	)
}

/** Mounted only while the dialog is open, so the search starts empty each time. */
function ShortcutsList() {
	const [query, setQuery] = useState('')
	const groups = filterShortcuts(SHORTCUT_GROUPS, query)

	return (
		<>
			<DialogHeader className='px-4 pt-4'>
				<DialogTitle>Keyboard shortcuts</DialogTitle>
			</DialogHeader>
			<div className='relative px-4'>
				<Search
					className='pointer-events-none absolute top-1/2 left-6 size-(--icon-standalone) -translate-y-1/2 text-muted-foreground'
					aria-hidden
				/>
				<Input
					type='search'
					value={query}
					onChange={e => setQuery(e.target.value)}
					placeholder='Search shortcuts'
					aria-label='Search shortcuts'
					className='pl-8'
					data-testid='shortcuts-search'
				/>
			</div>
			<div className='min-h-0 flex-1 overflow-y-auto px-2 pb-3'>
				{groups.map(group => (
					<section key={group.name} aria-label={group.name}>
						<h3 className='px-2 pt-3 pb-1 text-control font-normal text-muted-foreground'>{group.name}</h3>
						{group.items.map(item => (
							<div
								key={`${group.name}-${item.id}`}
								className='flex h-8 items-center gap-3 rounded-md px-2 text-body hover:bg-accent'
								data-testid={`shortcuts-row-${item.id}`}>
								<span className='min-w-0 flex-1 truncate'>{item.label}</span>
								<kbd className='rounded border bg-card px-1.5 font-sans text-control text-muted-foreground'>
									{item.keys}
								</kbd>
							</div>
						))}
					</section>
				))}
				{groups.length === 0 && (
					<p className='py-8 text-center text-body text-muted-foreground' data-testid='shortcuts-empty'>
						No shortcuts match "{query}"
					</p>
				)}
			</div>
		</>
	)
}
